"use client";
import { useState, useMemo } from "react";
import { BellRing, X } from "lucide-react";
import ImageComponent from "@core/helpers/client/components/image";
import { slide_priority } from "@core/helpers/image-priority";
import DeviceOnly from "@/components/DeviceOnly";
import { GALLERY_QUALITY, GALLERY_SIZES_DESKTOP, GALLERY_SIZES_MOBILE, GALLERY_SIZES_THUMB } from "./_gallerySizes";
import SwiperComponent from "@/components/ui/swiper-component";
import Share from "@/components/Share";
import LIkeButtonProduct from "@/components/LikeButtonProduct";

const ProductGallery = ({
    is_favorite,
    add_to_favorite_loading,
    add_product_to_favorites_handler,
    data,
    gallery = [],
}) => {

    const [activeIndex, setActiveIndex] = useState(0);
    const [open, setOpen] = useState(false);

    const activeImage = gallery?.[activeIndex];

    const slides = useMemo(
        () =>
            (gallery || []).map((item, index) => (
                <div key={index} className="flex items-center justify-center w-full">
                    <div
                        className="flex items-center justify-center w-full lg:w-[500px] h-[70vh] mx-auto aspect-square lg:rounded-lg lg:overflow-hidden" >
                        <ImageComponent
                            src={item?.url}
                            alt={item?.alt}
                            width={100}
                            height={100}
                            className="object-contain"
                            // لایت‌باکس: حداکثر ۵۰۰px در دسکتاپ، تمام‌عرض در موبایل
                            sizes="(min-width:1024px) 500px, 100vw"
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                </div>
            )),
        [gallery]
    );

    const thumbs = useMemo(
        () =>
            (gallery || []).map((item, i) => (
                <div
                    key={i}
                    className="w-full lg:w-[65px] h-[65px] rounded-lg overflow-hidden cursor-pointer mx-auto"
                >
                    <ImageComponent
                        src={item?.url}
                        alt={item?.alt}
                        width={100}
                        height={100}
                        // بندانگشتی لایت‌باکس: ۶۵px دسکتاپ، حدود یک‌چهارم عرض در موبایل
                        sizes="(min-width:1024px) 65px, 25vw"
                        style={{ width: '100%', height: '100%' }}
                        className="object-cover w-full h-full"
                    />
                </div>
            )),
        [gallery]
    );

    if (!gallery?.length) return null;

    return (
        <>
            {/* DESKTOP */}
            <DeviceOnly on="desktop">
                <div className="hidden lg:block">
                    <div className="relative">
                        <div className="flex items-center justify-center w-full aspect-square rounded-md overflow-hidden">
                            <ImageComponent
                                src={activeImage?.url}
                                alt={activeImage?.alt}
                                width={100}
                                height={100}
                                onClick={() => setOpen(true)}
                                // تصویر LCP صفحه‌ی محصول در دسکتاپ
                                priority_level="lcp"
                                // همان sizes/quality نسخه‌ی سرور (_gallerySizes.js) تا فایل preload شده دوباره دانلود نشود
                                sizes={GALLERY_SIZES_DESKTOP}
                                quality={GALLERY_QUALITY}
                                style={{ width: "100%", height: "100%" }}
                                className="w-full h-full object-cover cursor-pointer"
                            />
                        </div>

                        {/* icons */}
                        <div className="absolute top-2 right-2 flex flex-col gap-2">

                            <LIkeButtonProduct
                                {...{
                                    data,
                                    is_favorite,
                                    add_to_favorite_loading,
                                    add_product_to_favorites_handler,
                                    className: "flex items-center justify-center w-10 h-10 bg-jmt-white border border-jmt-neutral-200 hover:bg-jmt-neutral-100 rounded-full"
                                }}
                            />
                            {/* <div className="flex items-center justify-center w-10 h-10 bg-jmt-white border border-jmt-neutral-200 hover:bg-jmt-neutral-100 rounded-full" >
                                <BellRing size={22} />
                            </div> */}

                            <Share
                                className="flex items-center justify-center w-10 h-10 bg-jmt-white border border-jmt-neutral-200 hover:bg-jmt-neutral-100 rounded-full" />
                        </div>

                    </div>

                    {/* thumbnails */}
                    <div className="flex items-center gap-2 mt-3 overflow-x-auto thin-scrollbar">

                        {gallery.slice(0, 4).map((item, index) => {
                            const isLastThumbnail = index === 3 && gallery.length > 4;

                            return (
                                <button
                                    key={index}
                                    type="button"
                                    // نام دکمه فقط از alt تصویر می‌آمد و alt پنل اغلب خالی است (Lighthouse: button-name)
                                    aria-label={isLastThumbnail
                                        ? `مشاهده‌ی همه‌ی ${gallery.length} تصویر`
                                        : `نمایش تصویر ${index + 1} از ${gallery.length}`}
                                    aria-pressed={!isLastThumbnail ? index === activeIndex : undefined}
                                    onClick={() => {
                                        if (isLastThumbnail) {
                                            setOpen(true);
                                        } else {
                                            setActiveIndex(index);
                                        }
                                    }}
                                    className={`cursor-pointer relative border rounded-md overflow-hidden shrink-0 w-[88px] h-[88px]
                                    ${index === activeIndex
                                            ? "border-jmt-neutral-700"
                                            : "border-jmt-neutral-200"
                                        }`}
                                >
                                    <ImageComponent
                                        src={item.url}
                                        alt={item.alt}
                                        width={100}
                                        height={100}
                                        // بندانگشتی‌ها بالای صفحه‌اند: بی‌درنگ، ولی بدون preload
                                        priority_level="eager"
                                        sizes={GALLERY_SIZES_THUMB}
                                        quality={GALLERY_QUALITY}
                                        style={{ width: "100%", height: "100%" }}
                                        className="object-cover w-full h-full"
                                    />

                                    {isLastThumbnail && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm text-white">
                                            +{gallery.length - 4}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </DeviceOnly>

            {/* LIGHTBOX */}
            {open && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="گالری تصاویر محصول"
                    className="fixed inset-0 bg-black z-50 flex items-center justify-center"
                    onClick={() => setOpen(false)}
                >
                    <button
                        type="button"
                        aria-label="بستن گالری"
                        onClick={() => setOpen(false)}
                        className="absolute top-6 left-6 text-white z-60 cursor-pointer"
                    >
                        <X size={26} />
                    </button>

                    <div
                        className="w-full lg:max-w-[900px] max-h-[90vh]"
                        onClick={(e) => e.stopPropagation()} >

                        <SwiperComponent
                            slides={slides}
                            slidesPerView={1}
                            navigation={true}
                            navPosition="side"
                            loop={false}
                            grabCursor={true}
                            thumbnail={true}
                            thumbsSlidesPerView={4.5}
                            thumbsSpaceBetween={6}
                            thumbnail_custom_array={thumbs}
                            swiper_thumbs_className="w-full mt-6"
                            options={{
                                initialSlide: activeIndex,
                                speed: 400,
                            }}
                            thumb_breakpoints={{
                                768: {
                                    slidesPerView: 6
                                },
                                1024: {
                                    slidesPerView: 8
                                }
                            }}
                            onSlideChange={(i) => setActiveIndex(i)}
                        />
                    </div>
                </div>
            )}

            {/* MOBILE */}
            <DeviceOnly on="mobile">
                <div className="lg:hidden">
                    <SwiperComponent
                        slides={gallery.map((item, index) => (
                            <div
                                key={index}
                                onClick={() => {
                                    setActiveIndex(index);
                                    setOpen(true);
                                }}
                                className="flex items-center justify-center" >
                                <div className="max-h-[390px] h-[390px] w-full">
                                    <ImageComponent
                                        src={item.url}
                                        alt={item.alt}
                                        width={100}
                                        height={100}
                                        // اسلاید اول تصویر LCP موبایل است (همان فایل تصویر اصلی دسکتاپ)؛ بقیه lazy
                                        priority_level={slide_priority(index, { hero: true })}
                                        sizes={GALLERY_SIZES_MOBILE}
                                        quality={GALLERY_QUALITY}
                                        className="object-cover"
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>
                        ))}
                        slidesPerView={1}
                        spaceBetween={10}
                        pagination={true}
                        paginationType="fraction"
                        navigation={false}
                        loop={false}
                        grabCursor={true}
                        swiper_className="w-full pagination-custom"
                    />
                </div>
            </DeviceOnly>
        </>
    );
};

export default ProductGallery;
